import { useMemo, useState } from 'react';
import { Button, Field, Input, Modal, Textarea } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { fmtNumber } from '../../lib/format';
import { toast } from '../../lib/toast';
import {
  locationLabelsUrl,
  useBulkLocations,
  useCreateLocation,
  useDeleteLocation,
  useLocationDetail,
  useLocations,
  useUpdateLocation,
  type StockLocation,
} from './api';

// 1 pozice, 2–4 pozice, 5+ pozic.
const plural = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many);

/**
 * Skladové pozice (regál / police / kontejner). Každá má štítek s QR `LOC:<kód>`:
 * skenem pozice se v telefonu otevře, co na ní leží, inventura pozice nebo přesun.
 */

function LocationForm({ location, onClose }: { location: StockLocation | null; onClose: () => void }) {
  const [code, setCode] = useState(location?.code ?? '');
  const [name, setName] = useState(location?.name ?? '');
  const [zone, setZone] = useState(location?.zone ?? '');
  const create = useCreateLocation();
  const update = useUpdateLocation();
  const pending = create.isPending || update.isPending;
  const save = () => {
    const payload = { code: code.trim(), name: name.trim() || undefined, zone: zone.trim() || undefined };
    const opts = { onSuccess: onClose, onError: (e: unknown) => toast.error((e as ApiError).message) };
    if (location) update.mutate({ id: location.id, ...payload }, opts);
    else create.mutate(payload, opts);
  };
  return (
    <Modal title={location ? `Pozice ${location.code}` : 'Nová pozice'} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-[8rem_1fr] gap-3">
          <Field label="Kód *" htmlFor="loc-code" hint="např. A-01-3">
            <Input id="loc-code" className="uppercase" autoFocus value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Field label="Zóna" htmlFor="loc-zone" hint="dřevo, kovy, barvy, kontejner…">
            <Input id="loc-zone" list="loc-zones" value={zone} onChange={(e) => setZone(e.target.value)} />
          </Field>
        </div>
        <Field label="Název" htmlFor="loc-name" hint="Volitelně — „Regál A, police 3“">
          <Input id="loc-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Zrušit</Button>
          <Button disabled={!code.trim() || pending} onClick={save}>{pending ? 'Ukládám…' : 'Uložit'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function BulkDialog({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState('');
  const bulk = useBulkLocations();
  const rows = text.split(/\r?\n/).map((l) => l.split(/[;\t]/).map((c) => c.trim())).filter((c) => c.some(Boolean));
  const items = rows.filter((c) => c[0]).map((c) => ({ code: c[0], name: c[1] || undefined, zone: c[2] || undefined }));
  return (
    <Modal title="Hromadně přidat pozice" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-zinc-500">
          Řádek = pozice: <code>kód; název; zóna</code> (středník nebo tabulátor — jde vložit z Excelu). Existující kód se přejmenuje.
          Při přestavbě skladu stačí vložit nový seznam a vytisknout štítky.
        </p>
        <Textarea rows={10} className="font-mono text-xs" placeholder={'A-01-1; Regál A, police 1; Dřevo\nA-01-2; Regál A, police 2; Dřevo\nK-01; Kontejner 1; Kontejner'} value={text} onChange={(e) => setText(e.target.value)} />
        <p className="text-xs text-zinc-500">{items.length} {plural(items.length, 'pozice', 'pozice', 'pozic')} k uložení</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Zrušit</Button>
          <Button
            disabled={items.length === 0 || bulk.isPending}
            onClick={() =>
              bulk.mutate(items, {
                onSuccess: (r) => { toast.success(`Uloženo: ${r.created} nových, ${r.updated} upravených`); onClose(); },
                onError: (e) => toast.error((e as ApiError).message),
              })
            }
          >
            {bulk.isPending ? 'Ukládám…' : `Uložit ${items.length}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function LocationDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data } = useLocationDetail(id);
  return (
    <Modal title={data ? `${data.location.code}${data.location.name ? ` · ${data.location.name}` : ''}` : 'Pozice'} onClose={onClose}>
      {data && (
        <div className="space-y-3">
          {data.materials.length === 0 ? (
            <p className="text-sm text-zinc-400">Na pozici zatím nic není. Materiál sem dostaneš v detailu materiálu nebo skenem (Sklad → Skenovat → Přesun).</p>
          ) : (
            <ul className="divide-y divide-zinc-100 text-sm dark:divide-zinc-800">
              {data.materials.map((m) => (
                <li key={m.id} className="flex justify-between gap-2 py-1.5">
                  <span className="truncate">{m.name} <span className="font-mono text-xs text-zinc-400">{m.sku}</span></span>
                  <span className="shrink-0 tabular-nums">{fmtNumber(Number(m.stockQty))} {m.unit}</span>
                </li>
              ))}
            </ul>
          )}
          <a href={locationLabelsUrl([id])} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800">
            🏷 Štítek pozice
          </a>
        </div>
      )}
    </Modal>
  );
}

export default function LocationsPage() {
  const { data: locations, isLoading } = useLocations(true);
  const del = useDeleteLocation();
  const update = useUpdateLocation();
  const [editing, setEditing] = useState<StockLocation | null>(null);
  const [creating, setCreating] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [detail, setDetail] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const zones = useMemo(() => [...new Set((locations ?? []).map((l) => l.zone).filter((z): z is string => !!z))], [locations]);
  const shown = (locations ?? []).filter((l) => !q.trim() || `${l.code} ${l.name ?? ''} ${l.zone ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const onError = (e: unknown) => toast.error((e as ApiError).message);

  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pozice ve skladu</h1>
          <p className="text-sm text-zinc-500">Regály, police, kontejnery — každá se štítkem s QR. Sken pozice v telefonu ukáže, co na ní leží.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={locationLabelsUrl()} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800">🏷 Štítky všech</a>
          <Button variant="secondary" onClick={() => setBulk(true)}>Hromadně</Button>
          <Button onClick={() => setCreating(true)}>+ Pozice</Button>
        </div>
      </div>
      <datalist id="loc-zones">{zones.map((z) => <option key={z} value={z} />)}</datalist>

      {(locations?.length ?? 0) > 8 && <Input className="mb-3 max-w-xs" placeholder="Hledat pozici…" value={q} onChange={(e) => setQ(e.target.value)} />}
      {isLoading && <p className="text-sm text-zinc-500">Načítám…</p>}
      {locations && locations.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700">
          Zatím žádné pozice. Založ je po jedné, nebo hromadně vlož seznam (kód; název; zóna) a vytiskni štítky.
        </div>
      )}
      {shown.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 text-left text-xs uppercase tracking-wide text-zinc-500 dark:bg-zinc-900">
              <tr>
                <th className="px-3 py-2 font-medium">Kód</th>
                <th className="px-3 py-2 font-medium">Název</th>
                <th className="px-3 py-2 font-medium">Zóna</th>
                <th className="px-3 py-2 text-right font-medium">Materiálů</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {shown.map((l) => (
                <tr key={l.id} className={l.active ? '' : 'opacity-50'}>
                  <td className="px-3 py-2 font-mono font-semibold"><button onClick={() => setDetail(l.id)} className="hover:underline">{l.code}</button></td>
                  <td className="px-3 py-2">{l.name ?? '—'}{!l.active && <span className="ml-1 text-xs text-zinc-400">(neaktivní)</span>}</td>
                  <td className="px-3 py-2 text-zinc-500">{l.zone ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{l._count?.materials ?? 0}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <a href={locationLabelsUrl([l.id])} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800">Štítek</a>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(l)}>Upravit</Button>
                      <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: l.id, active: !l.active }, { onError })}>{l.active ? 'Deaktivovat' : 'Aktivovat'}</Button>
                      {(l._count?.materials ?? 0) === 0 && (
                        <Button size="sm" variant="ghost" className="text-red-600 dark:text-red-400" onClick={() => { if (window.confirm(`Smazat pozici ${l.code}?`)) del.mutate(l.id, { onError }); }}>Smazat</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <LocationForm location={null} onClose={() => setCreating(false)} />}
      {editing && <LocationForm location={editing} onClose={() => setEditing(null)} />}
      {bulk && <BulkDialog onClose={() => setBulk(false)} />}
      {detail && <LocationDetail id={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
